'use client';

import { useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import {
  datadogRum,
  DefaultPrivacyLevel,
  type RumInitConfiguration,
} from '@datadog/browser-rum';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSelectedLayoutSegments } from 'next/navigation';

import { beforeSendRumEvent as beforeSend } from './beforeSendRumEvent';
import { scheduleClearDatadogTestCookies } from './clearDatadogTestCookies';
import { getDatadogRumSampleRate } from './getRumSampleRate';

import { RQ_PREVIEW_EXPS_CACHE } from '@/modules/ReactQuery/cacheKeys';
import { TRANSCEND_CONSENT_CHANGE_EVENT_NAME } from '@/modules/consent/constants';
import { useConsentManager } from '@/modules/consent/hooks/useConsentManager';
import { type CachedExperiments } from '@/modules/experiments/types';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { DD_SERVICE } from '@/modules/observability/constants';
import { constructDynamicRoute } from '@/modules/routing/constructDynamicRoute';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function InitRealUserMonitoring() {
  const params = useParams();
  const segments = useSelectedLayoutSegments();
  const viewName = constructDynamicRoute(params, segments);
  const shouldInit = process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'development';

  const client = useQueryClient();
  const { language } = useCurrentLanguage();
  const { location } = useCurrentLocation();
  const [_, user] = useCurrentUser();
  const [didInit, setDidInit] = useState(false);

  const { airgap } = useConsentManager();

  const experiments = client.getQueryData<CachedExperiments>([
    RQ_PREVIEW_EXPS_CACHE,
  ]);

  function isAnalyticsConsented() {
    const consent = airgap?.getConsent();

    /*
    The logic here is as follows:
    1. We initialize RUM with trackingConsent as 'not-granted' to ensure we capture all performance entries from the start.
    2. Check if the user has confirmed their consent in airgap. If they haven't, we should not update the tracking consent.
    3. Finally, check if the user has given consent for Analytics in airgap. If they have, we can update their consent.
    Note: for most users in the US (e.g. everywhere but California as of 05/2026): Analytics consent will be set
    to 'Auto' which will resolve to true.
    */
    return Boolean(consent?.confirmed && Boolean(consent?.purposes?.Analytics));
  }

  function handleConsentChange() {
    // Update RUM tracking consent based on the user's decision
    datadogRum.setTrackingConsent(
      isAnalyticsConsented() ? 'granted' : 'not-granted'
    );
  }

  useEffect(() => {
    scheduleClearDatadogTestCookies();
  }, []);

  // Init RUM immediately with consent pending — this captures accurate
  // performance entries from page load. Data is buffered until consent
  // is resolved, then either flushed (granted) or discarded (not-granted).
  useEffect(() => {
    if (didInit || !shouldInit) {
      return;
    }
    const config: RumInitConfiguration = {
      applicationId: process.env.NEXT_PUBLIC_DD_RUM_APP_ID!,
      clientToken: process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN!,
      site: 'datadoghq.com',
      service: DD_SERVICE,
      env: process.env.NEXT_PUBLIC_DEPLOY_ENV,
      trackUserInteractions: true,
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
      allowedTracingUrls: [
        'https://webapi.depop.com',
        'https://webapi-staging.depop.com',
      ],
      version: process.env.NEXT_PUBLIC_APP_VERSION!,
      trackViewsManually: true,
      trackingConsent: 'not-granted',
      beforeSend,
      ...getDatadogRumSampleRate(process.env.NEXT_PUBLIC_DEPLOY_ENV!),
    };

    datadogRum.init(config);
    datadogRum.startSessionReplayRecording();

    const hasConfigs = !!window?.DD_RUM?.getInitConfiguration();
    if (hasConfigs) {
      setDidInit(true);
    }
  }, []);

  // Once airgap is ready, resolve consent state immediately.
  // Also listen for consent changes (e.g. user interacts with the banner).
  useEffect(() => {
    if (!airgap || !didInit) {
      return;
    }

    datadogRum.setTrackingConsent(
      isAnalyticsConsented() ? 'granted' : 'not-granted'
    );

    airgap.addEventListener(
      TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
      handleConsentChange
    );

    return () => {
      airgap.removeEventListener(
        TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
        handleConsentChange
      );
    };
  }, [airgap, didInit]);

  useEffect(() => {
    if (didInit && user?.id) {
      datadogLogs.setUser({ id: user?.id.toString() });
      datadogRum.setUser({ id: user?.id.toString(), name: user?.username });
    }
  }, [didInit, user?.id]);

  useEffect(() => {
    if (didInit) {
      const appContext = {
        language,
        locale: location,
        experiments,
        user: {
          isAuthenticated: Boolean(user?.id),
          date_joined: user?.date_joined,
          verified: user?.verified,
        },
      };
      datadogRum.setGlobalContext(appContext);
    }
  }, [didInit, user?.id, language]);

  useEffect(() => {
    if (didInit) {
      /**
       * We have set trackViewsManually as true, so that we can
       * group our metrics by their dynamic route, where possible.
       * e.g /products/[slug] for our product page
       */
      datadogRum.startView({
        name: viewName,
        service: DD_SERVICE,
        version: process.env.NEXT_PUBLIC_APP_VERSION,
      });
    }
  }, [didInit, viewName]);

  return null;
}
